/**
 * Minor propers — the studio site's DISPLAY logic over
 * `@gjl/music-manager/propers-pages`.
 *
 * The split matches `lib/hymnal.ts`: the page model (slugs, church-year order,
 * route enumeration) lives in the package so the app and `generate-seo-routes.mjs`
 * cannot compute different routes; search and labelling live here because they
 * are properties of this site, not of the data.
 *
 * Same two disciplines as `lib/hymnal.ts`:
 *
 * 1. EVERY HELPER IS PURE and takes the data as an argument, so the same code
 *    runs in SSR, in tests and in the browser. `data/propers.ts` is the only
 *    module that loads JSON.
 * 2. NEVER EMIT A DEAD END. Helpers return empty arrays rather than placeholder
 *    rows, so a template can decide not to render a section.
 *
 * WHY THIS EXISTS. The chant books are answerable by day only if you already know
 * which page to open. The American Gradual varies 81 of its chants by lectionary
 * year and offers 31 as numbered alternatives, and no front end exposes either.
 * Grouping by day and labelling by year is the whole product.
 */
import {
  enumerateProperDays,
  type ProperBook,
  type ProperChant,
  type ProperDayPage,
} from '@gjl/music-manager/propers-pages'

export type {
  ProperBook,
  ProperChant,
  ProperDayPage,
  ProperDay,
  ProperSource,
  RclYear,
} from '@gjl/music-manager/propers-pages'

export interface ProperIndex {
  sources: Array<ProperBook['source']>
  days: ProperDayPage[]
  bySlug: Map<string, ProperDayPage>
  /** Church-year groups, in order, with the non-temporal days last. */
  groups: Array<{ label: string; days: ProperDayPage[] }>
}

/** Build the site's index over one or more books. Pure: the caller supplies the data. */
export function indexProperBooks(...books: ProperBook[]): ProperIndex {
  const days = enumerateProperDays(...books)

  // GROUPED BY `day.group`, which is the season for the temporal cycle and one
  // explicit bucket for everything `dayOrder` ranks 99. Grouping on `season`
  // alone was tried and a test caught it: `seasonForDayId` files the
  // Presentation under Epiphany, correct about the calendar and wrong here,
  // because those days sort to the end and so emitted "Epiphany" a second time
  // after Pentecost. The bucket is what keeps each season stated once.
  //
  // Adjacency rather than bucketing, since the days already arrive in order: it
  // keeps the run order and never invents an empty group.
  const groups: ProperIndex['groups'] = []
  for (const day of days) {
    const tail = groups[groups.length - 1]
    if (tail && tail.label === day.group) tail.days.push(day)
    else groups.push({ label: day.group, days: [day] })
  }

  return {
    sources: books.map((b) => b.source),
    days,
    bySlug: new Map(days.map((d) => [d.slug, d])),
    groups,
  }
}

/** Chants appointed for a given lectionary year, plus those that serve every year. */
export function chantsForYear(chants: ProperChant[], year: 'A' | 'B' | 'C'): ProperChant[] {
  return chants.filter((c) => !c.years || c.years.includes(year))
}

/**
 * Free-text search across day title, incipit, citation and mode.
 *
 * Deliberately substring rather than token-prefix: the searches this has to
 * answer are "Ad te" (an incipit a musician half-remembers), "Psalm 25" (a
 * citation) and "proper 12" (a day). A prefix index would miss the middle of a
 * citation, which is where the psalm number lives.
 */
export function searchProperDays(days: ProperDayPage[], query: string): ProperDayPage[] {
  const q = query.trim().toLowerCase()
  if (!q) return days
  return days.filter((d) => {
    if (d.title.toLowerCase().includes(q)) return true
    if (d.dayId.replace(/-/g, ' ').includes(q)) return true
    return d.chants.some(
      (c) =>
        c.incipit?.toLowerCase().includes(q) ||
        c.citation?.toLowerCase().includes(q) ||
        (c.mode && `mode ${c.mode}` === q)
    )
  })
}

/** Human label for a chant's qualifier, e.g. "Year A" or "option 2, outside Lent". */
export function qualifierLabel(chant: ProperChant): string | null {
  const parts: string[] = []
  if (chant.years) parts.push(chant.years.length > 1 ? `Years ${chant.years.join(' and ')}` : `Year ${chant.years[0]}`)
  if (chant.option) parts.push(`option ${chant.option}`)
  if (chant.condition === 'in-lent') parts.push('in Lent')
  if (chant.condition === 'outside-lent') parts.push('outside Lent')
  return parts.length ? parts.join(', ') : null
}
